import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlfredModalActions, ExerciseActions } from './exercise.actions';
import { PractiseBookPageActions } from '../practise/practise.actions';
import { catchError, exhaustMap, filter, map, of } from 'rxjs';
import { ParagraphFacade } from '../paragraph/paragraph.facade';
import { ExaminationSessionService } from '../../examination-session.service';
import { isDefined } from '@lyceo/shared/util';
import { ModalController } from '@ionic/angular/standalone';
import { OverlayEventDetail } from '@ionic/core';
import {
  BookExam,
  BookSessionDto,
  ParagraphSessionDto,
} from '@lyceo/lyceo-pupil-app/practise/util-models';
import { ALFRED_CLIENT_MODAL } from '@lyceo/lyceo-pupil-app/practise/util';
import { IonicComponentRef } from '@lyceo/shared/util-models';

@Injectable()
export class ExerciseEffects {
  private readonly actions$ = inject(Actions);
  private readonly paragraphFacade = inject(ParagraphFacade);
  private readonly examinationSessionService = inject(
    ExaminationSessionService,
  );
  private readonly modalController: ModalController = inject(ModalController);
  private readonly alfredClientModal =
    inject<IonicComponentRef>(ALFRED_CLIENT_MODAL);

  public getParagraphSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PractiseBookPageActions.exerciseClicked),
      map(({ exercise }) => ({
        paragraphId: this.paragraphFacade.paramParagraphId(),
        questionnaireId: exercise.questionnaireId,
      })),
      filter(
        (
          payload,
        ): payload is { paragraphId: string; questionnaireId: string } =>
          isDefined(payload.paragraphId),
      ),
      exhaustMap(({ paragraphId, questionnaireId }) =>
        this.examinationSessionService
          .getParagraphSession(paragraphId, questionnaireId)
          .pipe(
            map((session) =>
              ExerciseActions.getParagraphSessionSuccess({ session }),
            ),
            catchError((error) =>
              of(ExerciseActions.getParagraphSessionFailure({ error })),
            ),
          ),
      ),
    );
  });

  public getBookSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PractiseBookPageActions.finalExamExerciseClicked),
      filter((action) => isDefined(action.book.finalExam)),
      exhaustMap((action) =>
        this.examinationSessionService
          .getBookSession(
            action.book.id,
            (action.book.finalExam as BookExam).questionnaireId,
          )
          .pipe(
            map((session) =>
              ExerciseActions.getBookSessionSuccess({ session }),
            ),
            catchError((error) =>
              of(
                ExerciseActions.getBookSessionFailure({
                  error,
                }),
              ),
            ),
          ),
      ),
    );
  });

  public openAlfredModal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        ExerciseActions.getParagraphSessionSuccess,
        ExerciseActions.getBookSessionSuccess,
      ),
      exhaustMap(async (action) => {
        const modal: HTMLIonModalElement = await this.modalController.create({
          component: this.alfredClientModal,
          componentProps: {
            session: action.session,
          },
          cssClass: ['alfredModal'],
        });
        await modal.present();
        return modal.onDidDismiss();
      }),
      map(
        (
          overlayEvent: OverlayEventDetail<
            ParagraphSessionDto | BookSessionDto
          >,
        ) => {
          if (overlayEvent.data) {
            return AlfredModalActions.sessionFinished({
              session: overlayEvent.data,
            });
          }

          return AlfredModalActions.modalClosed();
        },
      ),
    );
  });
}
